import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSidenav } from '@angular/material/sidenav';
import { ActivatedRoute, Router } from '@angular/router';
import { BlockUI, NgBlockUI } from 'ng-block-ui';
import { firstValueFrom } from 'rxjs';
import { MAX_MATCH_JOKERS } from '../../constants/joker.constants';
import { Badge } from '../../models/badge.model';
import {
  PollAiSuggestionRequest,
  PollAiSuggestionResult,
} from '../../models/poll-ai-suggestion.model';
import { AmplitudeService } from '../../services/amplitude.service';
import { ApiService } from '../../services/api.service';
import { AuthService } from '../../services/auth.service';
import { BadgesService } from '../../services/badges.service';
import { ConfettiService } from '../../services/confetti.service';
import { LocalService } from '../../services/local.service';
import { MatchesService } from '../../services/matches.service';
import { PollsService } from '../../services/polls.service';
import { SidenavService } from '../../services/sidenav.service';
import { SnackbarService } from '../../services/snackbar.service';
import { StandingsService } from '../../services/standings.service';
import { TeamsService } from '../../services/teams.service';
import { UsersService } from '../../services/users.service';
import { PromptModalComponent } from '../prompt-modal/prompt-modal.component';
import { BadgeUnlockModalComponent } from '../shared/badge-unlock-modal/badge-unlock-modal.component';
import { ConfirmModalComponent } from '../shared/confirm-modal/confirm-modal.component';
import { MatchesListComponent } from '../shared/matches-list/matches-list.component';

@Component({
  selector: 'app-poll',
  templateUrl: './poll.component.html',
  styleUrls: ['./poll.component.scss'],
  standalone: false,
})
export class PollComponent implements OnInit, OnDestroy {
  @ViewChild(MatchesListComponent)
  matchesListComponent: MatchesListComponent;
  @BlockUI() blockUI: NgBlockUI;

  sidenav: MatSidenav;
  user: any;
  userId: string;
  loggedUser: any;
  currentTimestamp: number;
  firstRoundIniTimestamp: number;
  currentRoundIniTimestamp: number;
  isAdmin: boolean;
  teams: any[];
  matchesViewMode: string;
  isMe: boolean = false;
  isAiAutofillLoading: boolean = false;
  pollProcessed: boolean = false;
  selectedRoundName: string = 'GROUP_STAGE';
  selectedRoundTitle: string = 'Fase de grupos';
  readonly maxMatchJokers = MAX_MATCH_JOKERS;
  readonly aiAutofillLoadingMessage =
    'La IA está haciendo magia con el balón...';

  pichichiName: string | null = null;

  private rounds: any[];
  private createMode: boolean = false;
  private pollId: string;
  private countdownInterval?: ReturnType<typeof setInterval>;
  private badgesCatalog: Badge[] = [];

  constructor(
    private sidenavService: SidenavService,
    private usersService: UsersService,
    private route: ActivatedRoute,
    private snackbar: SnackbarService,
    private authService: AuthService,
    private localService: LocalService,
    private badgesService: BadgesService,
    private confettiService: ConfettiService,
    private standingsService: StandingsService,
    private matchesService: MatchesService,
    private pollsService: PollsService,
    private teamsService: TeamsService,
    private router: Router,
    private amplitudeService: AmplitudeService,
    private apiService: ApiService,
    private dialog: MatDialog,
  ) {
    this.sidenav = this.sidenavService.getSidenav();
    this.loggedUser = this.authService.getUser();
    this.currentTimestamp = new Date().getTime() / 1000;
  }

  async ngOnInit() {
    if (!this.loggedUser?._id) {
      this.router.navigate(['/login']);
      return;
    }

    this.teams = await this.teamsService.getList();
    this.userId = this.route.snapshot.params['id'];

    if (!this.userId) {
      this.userId = this.loggedUser._id;
    }

    this.user = await this.usersService.get(this.userId);
    this.isMe = this.user._id === this.loggedUser._id;
    if (this.isMe) {
      await this.authService.updateCurrentUser(this.user);
      this.loggedUser = this.user;
    }
    this.matchesViewMode = this.isMe ? 'poll' : 'results';
    this.isAdmin = this.authService.isAdmin();
    this.rounds = await this.matchesService.getRounds();
    const badgesCatalogResponse = await this.badgesService.getCatalog();
    this.badgesCatalog = this.badgesService.normalizeCatalogResponse(
      badgesCatalogResponse,
    );
    this.firstRoundIniTimestamp = this.rounds[0].iniTimestamp;
    const activeRound = this.resolveActiveRound();
    this.currentRoundIniTimestamp = activeRound.iniTimestamp;
    this.selectedRoundName = activeRound.name;
    this.selectedRoundTitle = activeRound.title;
    this.startCountdownClock();
    await this.resolvePichichi();

    if (!this.user.jokers) {
      this.user.jokers = {
        champion: false,
        pichichi: false,
        matches: [],
      };
    }
    this.user.jokers.champion = false;
    this.user.jokers.pichichi = false;

    this.load();

    if (!this.isMe) {
      this.amplitudeService.logEvent('PollVisited', { user: this.user.email });
    }
  }

  ngOnDestroy(): void {
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
      this.countdownInterval = undefined;
    }
  }

  toggleMenu() {
    this.sidenavService.toggle();
  }

  goBack(): void {
    this.router.navigate([`/home/${this.userId}`]);
  }

  get canSavePredictions(): boolean {
    return (
      !!this.user &&
      (this.isAdmin ||
        (this.currentRoundIniTimestamp > this.currentTimestamp && this.isMe))
    );
  }

  get pendingRoundMatchesCount(): number {
    return this.matchesListComponent?.pendingRoundMatchesCount ?? 0;
  }

  get clearableMatchesCount(): number {
    return this.matchesListComponent?.clearableMatchesCount ?? 0;
  }

  get aiAutofillTooltip(): string {
    if (!this.pollProcessed) {
      return 'Cargando partidos';
    }

    if (this.pendingRoundMatchesCount === 0) {
      return 'No hay partidos pendientes en esta ronda';
    }

    return `Rellenar ${this.pendingRoundMatchesCount} ${this.getMatchLabel(this.pendingRoundMatchesCount)} con IA`;
  }

  get clearResultsTooltip(): string {
    if (!this.pollProcessed) {
      return 'Cargando partidos';
    }

    if (this.clearableMatchesCount === 0) {
      return 'No hay pronósticos editables para limpiar en esta ronda';
    }

    return 'Limpiar los resultados de la ronda seleccionada';
  }

  async autofillMissingResultsWithAi(): Promise<void> {
    const pendingMatches = this.getPendingRoundMatches();

    if (!this.canOpenAiAutofill(pendingMatches.length)) return;

    const confirmed = await this.confirmAiAutofill(pendingMatches.length);

    if (!confirmed) return;

    const extraPrompt = await this.collectAiAutofillInstructions();

    if (extraPrompt === null) return;

    await this.requestAiAutofill(pendingMatches, extraPrompt);
  }

  async clearAllResults(): Promise<void> {
    const clearableCount = this.clearableMatchesCount;

    if (!this.canClearResults(clearableCount)) return;

    const confirmed = await this.confirmClearResults(clearableCount);

    if (!confirmed) return;

    this.applyClearResults();
  }

  private resolveActiveRound(): any {
    const now = this.currentTimestamp;
    const started = this.rounds.filter((r) => r.iniTimestamp <= now);
    if (!started.length) {
      return this.rounds[0];
    }
    return started.reduce((prev, curr) =>
      curr.iniTimestamp > prev.iniTimestamp ? curr : prev,
    );
  }

  private async load() {
    const poll = await this.pollsService.get(this.user._id);

    if (!poll) {
      this.createMode = true;
      this.pollProcessed = true;
      return;
    }

    this.pollId = poll._id;
    this.populatePoll(poll);
  }

  private async populatePoll(poll: any) {
    this.createMode = false;

    if (!this.matchesListComponent?.matches) {
      setTimeout(() => {
        this.populatePoll(poll);
      }, 200);
      return;
    }

    const userJokerMatches = this.getPollUserJokerMatches(poll);

    poll.results.forEach((pollResult: any) => {
      const match = this.matchesListComponent.matches.find(
        (listMatch) => listMatch._id === pollResult.match._id,
      );

      // If we are not viewing our own results, then check we are on a date after round starts
      if (!this.isMe && !this.isAdmin) {
        const roundIniTimestamp = this.rounds.find(
          (item) => item.name === match.round,
        ).iniTimestamp;

        if (new Date().getTime() / 1000 < roundIniTimestamp) {
          match.homeGoals = null;
          match.awayGoals = null;
          match.penaltiesWinner = null;
          match.useJoker = null;
          match.points = null;
          return;
        }
      }

      match.homeGoals = pollResult.homeGoals;
      match.awayGoals = pollResult.awayGoals;
      match.penaltiesWinner = pollResult.penaltiesWinner;
      if (match.homeTeam) {
        match.homeTeam.checked =
          !!match.penaltiesWinner &&
          match.penaltiesWinner._id === match.homeTeam._id;
      }
      if (match.awayTeam) {
        match.awayTeam.checked =
          !!match.penaltiesWinner &&
          match.penaltiesWinner._id === match.awayTeam._id;
      }
      match.useJoker = userJokerMatches.includes(match._id);
      match.points = pollResult.points;

      // match.points = this.utilsService.calcMatchPointsFromResult(
      //   pollResult.match,
      //   pollResult,
      //   this.user
      // );
    });

    this.pollProcessed = true;
    this.matchesListComponent.scrollToLastPlayedMatch();
  }

  private getPollUserJokerMatches(poll: any): string[] {
    return poll.user?.jokers?.matches ?? this.user.jokers?.matches ?? [];
  }

  private getPendingRoundMatches(): any[] {
    return this.matchesListComponent?.getPendingRoundMatches() ?? [];
  }

  private canClearResults(clearableCount: number): boolean {
    if (!this.canSavePredictions || !this.pollProcessed) {
      return false;
    }

    if (clearableCount > 0) {
      return true;
    }

    this.snackbar.info('No hay pronósticos editables para limpiar.');
    return false;
  }

  private canOpenAiAutofill(pendingCount: number): boolean {
    if (!this.canSavePredictions || this.isAiAutofillLoading) {
      return false;
    }

    if (pendingCount > 0) {
      return true;
    }

    this.snackbar.info('No hay partidos pendientes en esta ronda.');
    return false;
  }

  private async confirmAiAutofill(pendingCount: number): Promise<boolean> {
    const confirmRef = this.dialog.open(ConfirmModalComponent, {
      data: this.buildAiAutofillDialogData(pendingCount),
    });

    return !!(await firstValueFrom(confirmRef.afterClosed()));
  }

  private buildAiAutofillDialogData(pendingCount: number) {
    const matchesLabel = this.getMatchLabel(pendingCount);

    return {
      eyebrow: 'IA',
      title: 'Rellenar pronósticos pendientes',
      message:
        `Se rellenarán <strong>${pendingCount}</strong> ${matchesLabel} de ` +
        `<strong>${this.selectedRoundTitle}</strong> con IA. ` +
        'Podrás revisar el resultado antes de pulsar "Guardar pronósticos".',
      icon: 'auto_awesome',
      tone: 'highlight',
      cancelText: 'Cancelar',
      confirmText: 'Rellenar',
    };
  }

  private async confirmClearResults(clearableCount: number): Promise<boolean> {
    const confirmRef = this.dialog.open(ConfirmModalComponent, {
      data: this.buildClearResultsDialogData(clearableCount),
    });

    return !!(await firstValueFrom(confirmRef.afterClosed()));
  }

  private buildClearResultsDialogData(clearableCount: number) {
    const matchesLabel = this.getMatchLabel(clearableCount);

    return {
      eyebrow: 'Limpieza',
      title: 'Limpiar pronósticos',
      message:
        `Se vaciarán <strong>${clearableCount}</strong> ${matchesLabel} de ` +
        `<strong>${this.selectedRoundTitle}</strong> que todavía puedes editar. ` +
        'También se quitarán penaltis y jokers de esos partidos.',
      icon: 'delete_sweep',
      tone: 'danger',
      cancelText: 'Cancelar',
      confirmText: 'Limpiar',
    };
  }

  private applyClearResults(): void {
    const clearedCount = this.matchesListComponent?.clearEditableResults() ?? 0;

    if (clearedCount === 0) {
      return;
    }

    const matchesLabel = this.getMatchLabel(clearedCount);
    this.snackbar.success(
      `Se han limpiado ${clearedCount} ${matchesLabel} de ${this.selectedRoundTitle}. Revisa y guarda.`,
    );
  }

  private async collectAiAutofillInstructions(): Promise<string | null> {
    const promptRef = this.dialog.open(PromptModalComponent, {
      panelClass: 'prompt-dialog',
      data: this.buildAiPromptDialogData(),
    });
    const value = await firstValueFrom(promptRef.afterClosed());

    return value === false || value === undefined ? null : value;
  }

  private buildAiPromptDialogData() {
    return {
      title: 'Instrucciones extra para la IA',
      description:
        'Añade una estrategia opcional para orientar los pronósticos. Si lo dejas vacío, la IA generará resultados sin indicaciones extra.',
      label: 'Prompt extra',
      placeholder:
        'Ej.: prioriza empates en partidos igualados y marcadores cortos en eliminatorias.',
      icon: 'psychology',
      acceptText: 'Generar',
      cancelText: 'Cancelar',
      multiline: true,
      required: false,
      rows: 5,
    };
  }

  private async requestAiAutofill(
    pendingMatches: any[],
    extraPrompt: string,
  ): Promise<void> {
    this.isAiAutofillLoading = true;
    this.blockUI.start(this.aiAutofillLoadingMessage);

    try {
      const request = this.buildAiSuggestionRequest(
        pendingMatches,
        extraPrompt,
      );
      const response = await this.pollsService.suggestMissingResultsWithAi(
        this.user._id,
        request,
      );

      const results = response?.results ?? [];
      this.amplitudeService.logEvent('AiAutofillUsed', {
        user: this.user.email,
        round: this.selectedRoundName,
        pendingMatches: pendingMatches.length,
        appliedResults: results.length,
        hasExtraPrompt: !!extraPrompt?.trim(),
        result: 'success',
      });
      this.applyAiAutofill(results);
    } catch (error) {
      this.amplitudeService.logEvent('AiAutofillUsed', {
        user: this.user.email,
        round: this.selectedRoundName,
        pendingMatches: pendingMatches.length,
        hasExtraPrompt: !!extraPrompt?.trim(),
        result: 'error',
      });
      this.handleAiAutofillError(error);
    } finally {
      this.isAiAutofillLoading = false;
      this.blockUI.stop();
    }
  }

  private buildAiSuggestionRequest(
    pendingMatches: any[],
    extraPrompt: string,
  ): PollAiSuggestionRequest {
    const normalizedPrompt = extraPrompt.trim();

    return {
      roundName: this.selectedRoundName,
      matchIds: pendingMatches.map((match) => match._id),
      extraPrompt: normalizedPrompt || undefined,
    };
  }

  private applyAiAutofill(results: PollAiSuggestionResult[]): void {
    const appliedCount =
      this.matchesListComponent?.applySuggestedResults(results) ?? 0;

    if (appliedCount === 0) {
      this.snackbar.info(
        'La IA no ha devuelto nuevos pronósticos para aplicar.',
      );
      return;
    }

    const matchesLabel = this.getMatchLabel(appliedCount);
    this.snackbar.success(
      `La IA ha rellenado ${appliedCount} ${matchesLabel}. Revisa y guarda.`,
    );
  }

  private handleAiAutofillError(error: any): void {
    this.snackbar.error(this.getAiAutofillErrorMessage(error));
  }

  private getAiAutofillErrorMessage(error: any): string {
    if (error?.status === 404) {
      return 'La API de IA todavía no está disponible.';
    }

    return error?.message || 'No se han podido generar pronósticos con IA.';
  }

  private getMatchLabel(count: number): string {
    return count === 1 ? 'partido' : 'partidos';
  }

  update() {
    if (this.matchesListComponent.hasInvalidScoresForSubmit()) {
      this.snackbar.warning(
        'Los resultados deben ser números enteros iguales o mayores que 0.',
      );
      return;
    }

    let results = this.matchesListComponent.getResults();
    let poll = {
      _id: '',
      user: {
        ...this.user,
        jokers: {
          ...(this.user.jokers ?? {}),
          champion: false,
          pichichi: false,
        },
      },
      results: results,
    };

    if (!this.checkResults(results)) {
      return;
    }

    // Check results
    let error = false;

    poll.results.forEach((pollResult: any) => {
      if (
        (pollResult.homeGoals !== null && pollResult.awayGoals === null) ||
        (pollResult.homeGoals === null && pollResult.awayGoals !== null)
      ) {
        if (!error) {
          this.snackbar.warning(
            'Tienes algún resultado a medio rellenar. Por favor, compruébalo.',
          );
          error = true;
        }
      }
    });

    if (error) {
      return;
    }

    this.blockUI.start();

    if (this.createMode) {
      this.pollsService
        .create(poll)
        .then(this.onSuccess.bind(this))
        .catch(this.onError.bind(this));
    } else {
      poll._id = this.pollId;
      this.pollsService
        .update(poll)
        .then(this.onSuccess.bind(this))
        .catch(this.onError.bind(this));
    }
  }

  private async onSuccess(poll: any) {
    this.blockUI.stop();
    this.user = await this.usersService.get(this.userId);
    if (this.isMe) {
      await this.authService.updateCurrentUser(this.user);
      this.loggedUser = this.user;
    }
    await this.resolvePichichi();
    await this.load();
    await this.checkNewBadgeUnlocks();
    this.snackbar.success('Has actualizado los datos correctamente');
    this.amplitudeService.logEvent('PollUpdated', { result: 'success' });
  }

  private onError(error: any) {
    this.blockUI.stop();

    if (error.code === 2001) {
      this.snackbar.warning(
        `No puedes poner un JOKER aquí. El máximo permitido es ${this.maxMatchJokers}.`,
      );
      return;
    }

    this.snackbar.error(error.message);
  }

  showChampionForm() {
    this.router.navigate([`/champion/${this.user._id}`]);
  }

  onRoundChanged(roundName: string) {
    this.selectedRoundName = roundName;
    const activeRoundIndex = this.rounds.findIndex(
      (round) => round.name === roundName,
    );
    this.currentRoundIniTimestamp = this.rounds[activeRoundIndex].iniTimestamp;
    this.selectedRoundTitle = this.rounds[activeRoundIndex].title;
  }

  get championTeam(): any {
    if (!this.user?.champion?._id || !this.teams) return null;
    return this.teams.find((t) => t._id === this.user.champion._id) ?? null;
  }

  get isRoundClosed(): boolean {
    return this.remainingRoundSeconds <= 0;
  }

  get remainingRoundSeconds(): number {
    if (!this.currentRoundIniTimestamp) {
      return 0;
    }

    return Math.max(0, this.currentRoundIniTimestamp - this.currentTimestamp);
  }

  get roundCountdownLabel(): string {
    if (this.isRoundClosed) {
      return 'Ronda cerrada';
    }

    const totalSeconds = this.remainingRoundSeconds;
    const months = Math.floor(totalSeconds / (86400 * 30));
    const days = Math.floor((totalSeconds % (86400 * 30)) / 86400);
    const hours = Math.floor((totalSeconds % 86400) / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    if (months > 0) {
      return `${months}mes${months > 1 ? 'es' : ''} ${days}d ${hours}h ${minutes}m ${seconds}s`;
    }
    if (days > 0) {
      return `${days}d ${hours}h ${minutes}m ${seconds}s`;
    }
    return `${hours}h ${minutes}m ${seconds}s`;
  }

  private async resolvePichichi(): Promise<void> {
    const pichichiId = this.getPichichiId(this.user?.pichichi);
    this.pichichiName = null;

    if (!pichichiId) {
      return;
    }

    try {
      const player: any = await this.apiService.request(
        `/players/${pichichiId}`,
        'GET',
      );
      this.pichichiName = player?.name ?? null;
    } catch {
      this.pichichiName = null;
    }
  }

  private getPichichiId(pichichi: any): string | null {
    if (!pichichi) {
      return null;
    }

    if (typeof pichichi === 'string') {
      return pichichi;
    }

    return pichichi._id ?? null;
  }

  private startCountdownClock(): void {
    this.countdownInterval = setInterval(() => {
      this.currentTimestamp = Math.floor(Date.now() / 1000);
    }, 1000);
  }

  private async checkNewBadgeUnlocks(): Promise<void> {
    if (!this.isMe) return;

    const storageKey = `badges-unlocked-${this.userId}`;
    const unlockedBadges = await this.getUnlockedBadgesFromApi();
    const unlockedNow = unlockedBadges.map((badge) => badge.id);
    const unlockedBefore =
      (await this.localService.getDataV2<string[]>(storageKey)) || [];
    const newUnlocked = unlockedNow.filter(
      (id) => !unlockedBefore.includes(id),
    );

    if (newUnlocked.length > 0) {
      await this.notifyNewBadges(unlockedBadges, newUnlocked);
    }

    await this.localService.saveDataV2(storageKey, unlockedNow);
  }

  private async getUnlockedBadgesFromApi(): Promise<Badge[]> {
    const response = await this.badgesService.getUserBadgesByUserId(
      this.userId,
    );
    return this.badgesService
      .normalizeUserBadgesResponse(response, this.badgesCatalog)
      .filter((badge) => badge.unlocked);
  }

  private async notifyNewBadges(
    unlockedBadges: Badge[],
    newUnlocked: string[],
  ): Promise<void> {
    const newUnlockedBadges = unlockedBadges.filter((badge) =>
      newUnlocked.includes(badge.id),
    );

    this.confettiService.launchCelebration();
    this.snackbar.success(`Nueva insignia desbloqueada: ${newUnlocked.length}`);
    this.amplitudeService.logEvent('BadgeEarned', { ids: newUnlocked });

    const dialogRef = this.dialog.open(BadgeUnlockModalComponent, {
      data: { badges: newUnlockedBadges },
      panelClass: 'badge-unlock-dialog',
    });
    const action = await firstValueFrom(dialogRef.afterClosed());

    if (action === 'open-book') {
      await this.router.navigate([`/home/${this.userId}`], {
        queryParams: { openBadgeBook: '1' },
      });
    }

    await this.localService.saveDataV2(
      `badges-recently-unlocked-${this.userId}`,
      newUnlocked,
    );
  }

  checkResults(results: any[]) {
    let errorsFound = false;

    results.forEach((result: any) => {
      if (result.round !== 'GROUP_STAGE' && !this.isAdmin) {
        if (
          result.roundIniTimestamp > this.currentTimestamp &&
          result.homeGoals === result.awayGoals &&
          result.homeGoals !== null &&
          !result.penaltiesWinner
        ) {
          errorsFound = true;
          this.snackbar.warning(
            'Hay algún partido en el que no has definido quién va a ganar por penalties.',
          );
          return false;
        }
      }

      return true;
    });

    return !errorsFound;
  }
}
